<script>
import { mapGetters } from 'vuex';
import { shouldBeUrl } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import SettingsSection from '../../../../components/SettingsSection.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import FacebookReauthorize from './facebook/Reauthorize.vue';
import MicrosoftReauthorize from './channels/microsoft/Reauthorize.vue';
import GoogleReauthorize from './channels/google/Reauthorize.vue';
import ZaloPersonalReconnect from './components/ZaloPersonalReconnect.vue';
import PreChatFormSettings from './PreChatForm/Settings.vue';
import WeeklyAvailability from './components/WeeklyAvailability.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import ConfigurationPage from './settingsPage/ConfigurationPage.vue';
import CollaboratorsPage from './settingsPage/CollaboratorsPage.vue';
import WidgetBuilder from './WidgetBuilder.vue';
import BotConfiguration from './components/BotConfiguration.vue';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import SenderNameExamplePreview from './components/SenderNameExamplePreview.vue';
import SectionLayout from '../../../../components-next/SectionLayout.vue';
import TabManager from '../aiSettings/components/TabManager.vue';
import Switch from 'v3/components/Form/Switch.vue';
import LucideIcon from '../../../../components/LucideIcon.vue';
export default {
  components: {
    BotConfiguration,
    CollaboratorsPage,
    ConfigurationPage,
    FacebookReauthorize,
    GreetingsEditor,
    PreChatFormSettings,
    SettingIntroBanner,
    SettingsSection,
    WeeklyAvailability,
    WidgetBuilder,
    SenderNameExamplePreview,
    MicrosoftReauthorize,
    GoogleReauthorize,
    ZaloPersonalReconnect,
    SectionLayout,
    TabManager,
    Switch,
  },
  mixins: [inboxMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: '',
      greetingEnabled: true,
      greetingMessage: '',
      emailCollectEnabled: false,
      csatSurveyEnabled: false,
      senderNameType: 'friendly',
      businessName: '',
      locktoSingleConversation: false,
      allowMessagesAfterResolved: true,
      continuityViaEmail: true,
      selectedInboxName: '',
      channelWebsiteUrl: '',
      webhookUrl: '',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      selectedFeatureFlags: [],
      replyTime: '',
      selectedTabIndex: 0,
      activeTab: '',
      selectedPortalSlug: '',
      showBusinessNameInput: false,
      aiBotEnabled: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'inboxes/getUIFlags',
      portals: 'portals/allPortals',
    }),
    canUseAiCore() {
      if (!this.accountId) return false;
      return this.isFeatureEnabledonAccount(this.accountId, 'ai_core');
    },
    showBackButton() {
      return this.$route.meta?.showBackButton || false;
    },
    backUrl() {
      if (this.$route.meta?.backTo) {
        return { name: this.$route.meta.backTo };
      }
      return '';
    },
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
    managerTabs() {
      return this.tabs.map(t => ({ key: t.key, name: t.name }));
    },
    tabBarTabs() {
      return this.tabs.map(tab => ({
        label: tab.name,
        value: tab.key,
      }));
    },
    whatsAppAPIProviderName() {
      if (this.isAWhatsAppCloudChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD');
      }
      if (this.is360DialogWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.360_DIALOG');
      }
      if (this.isATwilioWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO');
      }
      return '';
    },
    tabs() {
      let visibleToAllChannelTabs = [
        {
          key: 'inbox_settings',
          name: this.$t('INBOX_MGMT.TABS.SETTINGS'),
        },
        {
          key: 'collaborators',
          name: this.$t('INBOX_MGMT.TABS.COLLABORATORS'),
        },
        {
          key: 'businesshours',
          name: this.$t('INBOX_MGMT.TABS.BUSINESS_HOURS'),
        },
      ];

      if (this.isAWebWidgetInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'preChatForm',
            name: this.$t('INBOX_MGMT.TABS.PRE_CHAT_FORM'),
          },
          {
            key: 'widgetBuilder',
            name: this.$t('INBOX_MGMT.TABS.WIDGET_BUILDER'),
          },
        ];
      }

      if (
        this.isATwilioChannel ||
        this.isALineChannel ||
        this.isAPIInbox ||
        (this.isAnEmailChannel && !this.inbox.provider) ||
        this.isAMicrosoftInbox ||
        this.isAGoogleInbox ||
        this.isAWhatsAppChannel ||
        this.isAWebWidgetInbox
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'configuration',
            name: this.$t('INBOX_MGMT.TABS.CONFIGURATION'),
          },
        ];
      }

      if (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.AGENT_BOTS
        ) &&
        !(this.isAnEmailChannel || this.isATwitterInbox) && false
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'botConfiguration',
            name: this.$t('INBOX_MGMT.TABS.BOT_CONFIGURATION'),
          },
        ];
      }
      return visibleToAllChannelTabs;
    },
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentInboxId);
    },

    inboxName() {
      if (this.isATwilioSMSChannel || this.isATwilioWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.messaging_service_sid || this.inbox.phone_number
          })`;
      }
      if (this.isAWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.phone_number})`;
      }
      if (this.isAnEmailChannel) {
        return `${this.inbox.name} (${this.inbox.email})`;
      }
      return this.inbox.name;
    },
    canLocktoSingleConversation() {
      return (
        this.isASmsInbox ||
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAPIInbox
      );
    },
    inboxNameLabel() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL');
    },
    inboxNamePlaceHolder() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER');
    },
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
    microsoftUnauthorized() {
      return this.isAMicrosoftInbox && this.inbox.reauthorization_required;
    },
    facebookUnauthorized() {
      return this.isAFacebookInbox && this.inbox.reauthorization_required;
    },
    googleUnauthorized() {
      const isLegacyInbox = ['imap.gmail.com', 'imap.google.com'].includes(
        this.inbox.imap_address
      );

      return (
        (this.isAGoogleInbox || isLegacyInbox) &&
        this.inbox.reauthorization_required
      );
    },
    zaloPersonalUnauthorized() {
      return this.isAZaloPersonalChannel && this.inbox.reauthorization_required;
    },
    // Feature flag computed properties for Switch components
    attachmentsEnabled: {
      get() {
        return this.selectedFeatureFlags.includes('attachments');
      },
      set(value) {
        if (value) {
          if (!this.selectedFeatureFlags.includes('attachments')) {
            this.selectedFeatureFlags.push('attachments');
          }
        } else {
          this.selectedFeatureFlags = this.selectedFeatureFlags.filter(
            flag => flag !== 'attachments'
          );
        }
      },
    },
    emojiPickerEnabled: {
      get() {
        return this.selectedFeatureFlags.includes('emoji_picker');
      },
      set(value) {
        if (value) {
          if (!this.selectedFeatureFlags.includes('emoji_picker')) {
            this.selectedFeatureFlags.push('emoji_picker');
          }
        } else {
          this.selectedFeatureFlags = this.selectedFeatureFlags.filter(
            flag => flag !== 'emoji_picker'
          );
        }
      },
    },
    endConversationEnabled: {
      get() {
        return this.selectedFeatureFlags.includes('end_conversation');
      },
      set(value) {
        if (value) {
          if (!this.selectedFeatureFlags.includes('end_conversation')) {
            this.selectedFeatureFlags.push('end_conversation');
          }
        } else {
          this.selectedFeatureFlags = this.selectedFeatureFlags.filter(
            flag => flag !== 'end_conversation'
          );
        }
      },
    },
    useInboxAvatarForBotEnabled: {
      get() {
        return this.selectedFeatureFlags.includes('use_inbox_avatar_for_bot');
      },
      set(value) {
        if (value) {
          if (!this.selectedFeatureFlags.includes('use_inbox_avatar_for_bot')) {
            this.selectedFeatureFlags.push('use_inbox_avatar_for_bot');
          }
        } else {
          this.selectedFeatureFlags = this.selectedFeatureFlags.filter(
            flag => flag !== 'use_inbox_avatar_for_bot'
          );
        }
      },
    },
  },
  watch: {
    activeTab(newKey) {
      const idx = this.tabs.findIndex(t => t.key === newKey);
      if (idx !== -1) this.selectedTabIndex = idx;
    },
    $route(to) {
      if (to.name === 'settings_inbox_show') {
        this.fetchInboxSettings();
      }
    },
  },
  mounted() {
    this.fetchInboxSettings();
    this.fetchPortals();
    this.activeTab = this.tabs[0]?.key || '';
  },
  methods: {
    fetchPortals() {
      this.$store.dispatch('portals/index');
    },
    handleFeatureFlag(e) {
      this.selectedFeatureFlags = this.toggleInput(
        this.selectedFeatureFlags,
        e.target.value
      );
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
    },
    handleTabChange(tab) {
      const tabIndex = this.tabs.findIndex(t => t.key === tab.value);
      if (tabIndex !== -1) {
        this.selectedTabIndex = tabIndex;
      }
    },
    async toggleChatbot() {
      const previousState = this.aiBotEnabled;
      this.aiBotEnabled = !this.aiBotEnabled;
      
      try {
        await this.updateInbox();
      } catch (error) {
        this.aiBotEnabled = previousState;
      }
    },
    fetchInboxSettings() {
      this.selectedTabIndex = 0;
      this.selectedAgents = [];
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      this.$store.dispatch('labels/get');
      this.$store.dispatch('inboxes/getWithoutCache').then(() => {
        this.aiBotEnabled = this.inbox.is_bot;
        this.avatarUrl = this.inbox.avatar_url;
        this.selectedInboxName = this.inbox.name;
        this.webhookUrl = this.inbox.webhook_url;
        this.greetingEnabled = this.inbox.greeting_enabled || false;
        this.greetingMessage = this.inbox.greeting_message || '';
        this.emailCollectEnabled = this.inbox.enable_email_collect;
        this.csatSurveyEnabled = this.inbox.csat_survey_enabled;
        this.senderNameType =
          this.inbox.sender_name_type === 'atc'
            ? 'friendly'
            : this.inbox.sender_name_type;
        this.businessName = this.inbox.business_name;
        this.allowMessagesAfterResolved =
          this.inbox.allow_messages_after_resolved;
        this.continuityViaEmail = this.inbox.continuity_via_email;
        this.channelWebsiteUrl = this.inbox.website_url;
        this.channelWelcomeTitle = this.inbox.welcome_title;
        this.channelWelcomeTagline = this.inbox.welcome_tagline;
        this.selectedFeatureFlags = this.inbox.selected_feature_flags || [];
        this.replyTime = this.inbox.reply_time;
        this.locktoSingleConversation = this.inbox.lock_to_single_conversation;
        this.selectedPortalSlug = this.inbox.help_center
          ? this.inbox.help_center.slug
          : '';
      });
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.currentInboxId,
          name: this.selectedInboxName,
          is_bot: this.aiBotEnabled,
          enable_email_collect: this.emailCollectEnabled,
          csat_survey_enabled: this.csatSurveyEnabled,
          allow_messages_after_resolved: this.allowMessagesAfterResolved,
          greeting_enabled: this.greetingEnabled,
          greeting_message: this.greetingMessage || '',
          portal_id: this.selectedPortalSlug
            ? this.portals.find(
              portal => portal.slug === this.selectedPortalSlug
            ).id
            : null,
          lock_to_single_conversation: this.locktoSingleConversation,
          sender_name_type:
            this.senderNameType === 'friendly'
              ? 'atc'
              : this.senderNameType,
          business_name: this.businessName || null,
          channel: {
            widget_color: this.inbox.widget_color,
            website_url: this.channelWebsiteUrl,
            webhook_url: this.webhookUrl,
            welcome_title: this.channelWelcomeTitle || '',
            welcome_tagline: this.channelWelcomeTagline || '',
            selectedFeatureFlags: this.selectedFeatureFlags,
            reply_time: this.replyTime || 'in_a_few_minutes',
            continuity_via_email: this.continuityViaEmail,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'), {type: 'success'});
      } catch (error) {
        if (error.response?.data?.error_code === 'AI_CONFIGURATION_INVALID' || 
            error.response?.data?.error_code === 'AI_CONNECTION_FAILED') {
          useAlert(error.response.data.error || 'Cấu hình AI chưa đầy đủ', {type: 'info'});
          this.aiBotEnabled = false;
        } else {
          useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'), {type: 'error'});
        }
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        useAlert('Chỉ chấp nhận file JPG hoặc PNG', {type: 'info'});
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        useAlert('Kích thước file không được vượt quá 5MB', {type: 'info'});
        return;
      }

      // Validate image dimensions
      const img = new Image();
      img.onload = () => {
        if (img.width > 600 || img.height > 600) {
          useAlert('Kích thước ảnh không được vượt quá 600x600 pixel', {type: 'info'});
          return;
        }

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          this.avatarUrl = e.target.result;
          this.avatarFile = file;
        };
        reader.readAsDataURL(file);
      };
      img.src = URL.createObjectURL(file);
    },

    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },

    async handleAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxAvatar',
          this.currentInboxId
        );
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(this.$t('INBOX_MGMT.DELETE.API.AVATAR_SUCCESS_MESSAGE'), {type: 'success'});
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('INBOX_MGMT.DELETE.API.AVATAR_ERROR_MESSAGE')
        , {type: 'error'});
      }
    },
    toggleSenderNameType(key) {
      this.senderNameType = key;
    },
    onClickShowBusinessNameInput() {
      this.showBusinessNameInput = !this.showBusinessNameInput;
      if (this.showBusinessNameInput) {
        this.$nextTick(() => {
          this.$refs.businessNameInput.focus();
        });
      }
    },
  },
  validations: {
    webhookUrl: {
      shouldBeUrl,
    },
    selectedInboxName: {},
  },
};
</script>

<template>
  <SectionLayout :title="inboxName" :show-back-button="showBackButton" :back-url="backUrl">
    <div class="w-full flex flex-col sm:flex-row gap-4 sm:gap-6">
      <!-- Mobile: Tab manager with dropdown | Tablet+: Left sidebar -->
      <div class="w-full sm:w-64 shrink-0 sm:h-[calc(100vh-110px)] md:sticky bg-white dark:bg-slate-800 rounded-2xl shadow-xl sm:overflow-auto">
        <TabManager v-model:active-tab="activeTab" :tabs="managerTabs" />
      </div>

      <!-- Right column: Page content -->
      <div class="flex-1 min-w-0 sm:max-h-[85vh] sm:overflow-y-auto rounded-lg">
        <!-- Rest of content -->
        <div v-if="selectedTabKey === 'inbox_settings'">
          <div class="flex flex-col gap-6 bg-n-gray-1 dark:bg-n-vars-black-br/30 p-0 md:p-6 rounded-lg">
            <div class="w-full">
              <div>
                <!-- Avatar Display -->
                <div class="flex flex-col items-center">
                  <!-- Profile Picture -->
                  <div class="relative mb-6">
                    <woot-thumbnail size="64px" :src="avatarUrl" :username="selectedInboxName" />
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex gap-3 mb-3">
                    <!-- Delete Button -->
                    <button v-if="avatarUrl" @click="handleAvatarDelete"
                      class="px-4 py-2 bg-n-main-1/15 border border-n-main-1/17 hover:bg-red-700 text-n-main-1 rounded-lg transition-colors duration-200 flex items-center gap-2">
                      {{ $t('INBOX_MGMT.ADD.AVATAR.DELETE') }}
                    </button>

                    <!-- Upload Button -->
                    <label
                      class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center gap-2 cursor-pointer">
                      {{ $t('INBOX_MGMT.ADD.AVATAR.UPLOAD') }}
                      <LucideIcon name="upload" size="16" />
                      <input ref="fileInput" type="file" accept="image/jpeg,image/png" class="hidden"
                        @change="handleFileSelect" />
                    </label>
                  </div>

                  <!-- Upload Requirements Text -->
                  <p class="text-sm text-n-gray-6 dark:text-n-gray-6 text-left flex justify-center">
                    {{ $t('INBOX_MGMT.ADD.AVATAR.UPLOAD_REQUIREMENTS') }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="facebookUnauthorized">
              <FacebookReauthorize :inbox="inbox" />
            </div>
            
            <div v-if="microsoftUnauthorized">
              <MicrosoftReauthorize :inbox="inbox" />
            </div>
            
            <div v-if="googleUnauthorized">
              <GoogleReauthorize :inbox="inbox" />
            </div>
            
            <div v-if="zaloPersonalUnauthorized">
              <ZaloPersonalReconnect :inbox="inbox" />
            </div>

            <div class="w-full flex flex-col gap-6">
              <SettingsSection :show-border="false">
                <!-- Custom Avatar Uploader -->
                <!-- Form fields with gap instead of pb-4 -->
                <div class="flex flex-col gap-6">
                  <woot-input v-model="selectedInboxName" :class="{ error: v$.selectedInboxName.$error }"
                    :label="inboxNameLabel" :placeholder="inboxNamePlaceHolder" :error="v$.selectedInboxName.$error
                      ? $t('INBOX_MGMT.ADD.CHANNEL_NAME.ERROR')
                      : ''
                      " @blur="v$.selectedInboxName.$touch" />

                  <woot-input v-if="isAPIInbox" v-model="webhookUrl" :class="{ error: v$.webhookUrl.$error }" :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.LABEL')
                    " :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.PLACEHOLDER')
                  " :error="v$.webhookUrl.$error
                  ? $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.ERROR')
                  : ''
                  " @blur="v$.webhookUrl.$touch" />

                  <woot-input v-if="isAWebWidgetInbox" v-model="channelWebsiteUrl"
                    :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')" :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER')
                      " />

                  <woot-input v-if="isAWebWidgetInbox" v-model="channelWelcomeTitle" :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL')
                    " :placeholder="$t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
                )
                  " />

                  <woot-input v-if="isAWebWidgetInbox" v-model="channelWelcomeTagline" :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL')
                    " :placeholder="$t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
                )
                  " />

                  <label v-if="isAWebWidgetInbox">
                    {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL') }}
                    <woot-color-picker v-model="inbox.widget_color" />
                  </label>

                  <label v-if="isAWhatsAppChannel">
                    {{ $t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.LABEL') }}
                    <input v-model="whatsAppAPIProviderName" type="text" disabled />
                  </label>

                  <!-- Greeting Toggle with Switch -->
                  <div class="w-full flex flex-col gap-3">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{
                          $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL')
                        }}
                      </label>
                      <Switch v-model="greetingEnabled" />
                    </div>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                      {{
                        $t(
                          'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
                        )
                      }}
                    </p>
                  </div>

                  <div v-if="greetingEnabled">
                    <GreetingsEditor v-model="greetingMessage" :label="$t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                    )
                      " :placeholder="$t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                  )
                    " :richtext="!textAreaChannels" />
                  </div>

                  <label v-if="isAWebWidgetInbox">
                    {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.TITLE') }}
                    <select v-model="replyTime">
                      <option key="in_a_few_minutes" value="in_a_few_minutes">
                        {{
                          $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_MINUTES')
                        }}
                      </option>
                      <option key="in_a_few_hours" value="in_a_few_hours">
                        {{
                          $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_HOURS')
                        }}
                      </option>
                      <option key="in_a_day" value="in_a_day">
                        {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_DAY') }}
                      </option>
                    </select>

                    <p class="pb-1 text-sm not-italic text-slate-600 dark:text-slate-400">
                      {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.HELP_TEXT') }}
                    </p>
                  </label>

                  <!-- Email Collect Toggle with Switch -->
                  <div v-if="isAWebWidgetInbox" class="w-full flex flex-col gap-3">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX') }}
                      </label>
                      <Switch v-model="emailCollectEnabled" />
                    </div>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                      {{
                        $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX_SUB_TEXT')
                      }}
                    </p>
                  </div>

                  <!-- CSAT Survey Toggle with Switch -->
                  <div class="w-full flex flex-col gap-3">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CSAT') }}
                      </label>
                      <Switch v-model="csatSurveyEnabled" />
                    </div>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                      {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CSAT_SUB_TEXT') }}
                    </p>
                  </div>

                  <!-- Allow Messages After Resolved Toggle with Switch -->
                  <div v-if="isAWebWidgetInbox" class="w-full flex flex-col gap-3">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED') }}
                      </label>
                      <Switch v-model="allowMessagesAfterResolved" />
                    </div>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                      {{
                        $t(
                          'INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED_SUB_TEXT'
                        )
                      }}
                    </p>
                  </div>

                  <!-- Continuity Via Email Toggle with Switch -->
                  <div v-if="isAWebWidgetInbox" class="w-full flex flex-col gap-3">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL') }}
                      </label>
                      <Switch v-model="continuityViaEmail" />
                    </div>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                      {{
                        $t(
                          'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL_SUB_TEXT'
                        )
                      }}
                    </p>
                  </div>

                  <div>
                    <label>
                      {{ $t('INBOX_MGMT.HELP_CENTER.LABEL') }}
                    </label>
                    <select v-model="selectedPortalSlug" class="filter__question">
                      <option value="">
                        {{ $t('INBOX_MGMT.HELP_CENTER.PLACEHOLDER') }}
                      </option>
                      <option v-for="p in portals" :key="p.slug" :value="p.slug">
                        {{ p.name }}
                      </option>
                    </select>
                    <!-- <p class="pb-1 text-sm not-italic text-slate-600 dark:text-slate-400">
                      {{ $t('INBOX_MGMT.HELP_CENTER.SUB_TEXT') }}
                    </p> -->
                  </div>

                  <!-- Lock to Single Conversation Toggle with Switch -->
                  <div v-if="canLocktoSingleConversation" class="w-full flex flex-col gap-3">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION') }}
                      </label>
                      <Switch v-model="locktoSingleConversation" />
                    </div>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                      {{
                        $t(
                          'INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION_SUB_TEXT'
                        )
                      }}
                    </p>
                  </div>

                  <label v-if="isAWebWidgetInbox">
                    {{ $t('INBOX_MGMT.FEATURES.LABEL') }}
                  </label>

                  <!-- Feature Flags with Switch -->
                  <div v-if="isAWebWidgetInbox" class="w-full">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.FEATURES.DISPLAY_FILE_PICKER') }}
                      </label>
                      <Switch v-model="attachmentsEnabled" />
                    </div>
                  </div>

                  <div v-if="isAWebWidgetInbox" class="w-full">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.FEATURES.DISPLAY_EMOJI_PICKER') }}
                      </label>
                      <Switch v-model="emojiPickerEnabled" />
                    </div>
                  </div>

                  <div v-if="isAWebWidgetInbox" class="w-full">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.FEATURES.ALLOW_END_CONVERSATION') }}
                      </label>
                      <Switch v-model="endConversationEnabled" />
                    </div>
                  </div>

                  <div v-if="isAWebWidgetInbox" class="w-full">
                    <div class="flex items-center justify-between w-full sm:w-2/5">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                        {{ $t('INBOX_MGMT.FEATURES.USE_INBOX_AVATAR_FOR_BOT') }}
                      </label>
                      <Switch v-model="useInboxAvatarForBotEnabled" />
                    </div>
                  </div>

                </div>
              </SettingsSection>

              <!-- Rest of sections remain the same -->
              <SettingsSection v-if="isAWebWidgetInbox || isAnEmailChannel"
                :title="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.TITLE')"
                :sub-title="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.SUB_TEXT')" :show-border="false">
                <div class="flex flex-col gap-4">
                  <SenderNameExamplePreview :sender-name-type="senderNameType" :business-name="businessName"
                    @update="toggleSenderNameType" />
                  <div class="flex flex-col items-start gap-2">
                    <woot-button variant="clear" color-scheme="primary" @click="onClickShowBusinessNameInput">
                      {{
                        $t(
                          'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.BUTTON_TEXT'
                        )
                      }}
                    </woot-button>
                    <div v-if="showBusinessNameInput" class="flex gap-2 w-[80%]">
                      <input ref="businessNameInput" v-model="businessName" :placeholder="$t(
                        'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.PLACEHOLDER'
                      )
                        " class="mb-0" type="text" />
                      <woot-button color-scheme="success" @click="updateInbox" class="!p-0">
                        {{
                          $t(
                            'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.SAVE_BUTTON_TEXT'
                          )
                        }}
                      </woot-button>
                    </div>
                  </div>
                </div>
              </SettingsSection>

              <SettingsSection v-if="canUseAiCore" :show-border="false">
                <!-- AI BOT Toggle with Switch -->
                <div class="w-full">
                  <div class="flex items-center justify-between w-full sm:w-2/5">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
                      Turn On Chatbot
                    </label>
                    <Switch v-model="aiBotEnabled" />
                  </div>
                </div>
              </SettingsSection>

              <SettingsSection :show-border="false">
                <div class="mt-12 flex justify-center">
                  <woot-submit-button class="!bg-n-bluegray-4 !text-white rounded-lg" v-if="isAPIInbox" type="submit"
                    :disabled="v$.webhookUrl.$invalid" :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
                    :loading="uiFlags.isUpdating" @click="updateInbox" />
                  <woot-submit-button class="!bg-n-bluegray-4 !text-white rounded-lg" v-else type="submit"
                    :disabled="v$.$invalid" :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
                    :loading="uiFlags.isUpdating" @click="updateInbox" />
                </div>
              </SettingsSection>
            </div>
          </div>
        </div>

        <div v-if="selectedTabKey === 'collaborators'">
          <CollaboratorsPage :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'configuration'">
          <ConfigurationPage :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'preChatForm'">
          <PreChatFormSettings :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'businesshours'">
          <WeeklyAvailability :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'widgetBuilder'">
          <WidgetBuilder :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'botConfiguration'">
          <BotConfiguration :inbox="inbox" />
        </div>
      </div>
    </div>
  </SectionLayout>
</template>

<style scoped lang="scss">
.settings--tabs {
  ::v-deep .tabs {
    @apply p-0;
  }
}

::v-deep .input-container {
  .label-text {
    @apply block dark:text-n-gray-25 mb-3;

  }
}
</style>